import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../../../api/api";

import Swal from "sweetalert2";

import {
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Chip,
  Divider,
} from "@mui/material";

import {
  QrCode2,
  Download,
  Inventory2,
  AttachMoney,
  Refresh,
} from "@mui/icons-material";

import {
  PDFDownloadLink,
} from "@react-pdf/renderer";

import ProductListQRScanPDF from "./ProductListQRScanPDF";


/*
|--------------------------------------------------------------------------
| PRODUCT QR MANAGEMENT
|--------------------------------------------------------------------------
*/

const ProductQRCode = () => {

  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(false);

  const [selectedPrice, setSelectedPrice] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | GET PRODUCT QR TOKEN
  |--------------------------------------------------------------------------
  */

  const getProductQRToken = useCallback(
    (product) => {

      if (!product) {
        return null;
      }

      if (
        product.qr_token !== null &&
        product.qr_token !== undefined &&
        String(product.qr_token).trim() !== ""
      ) {
        return String(
          product.qr_token
        ).trim();
      }

      if (
        product.qr_payload &&
        product.qr_payload.qr_token
      ) {
        return String(
          product.qr_payload.qr_token
        ).trim();
      }

      return null;
    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | NORMALIZE PRODUCT
  |--------------------------------------------------------------------------
  */

  const normalizeProduct = useCallback(
    (product) => {

      const qrToken =
        getProductQRToken(product);

      return {
        ...product,

        qr_token: qrToken,

        qr_payload: {
          ...(product.qr_payload || {}),

          type:
            product.qr_payload?.type ||
            "product",

          version:
            product.qr_payload?.version ||
            1,

          product_id:
            product.qr_payload?.product_id ||
            product.id,

          qr_token:
            qrToken,
        },
      };

    },
    [getProductQRToken]
  );


  /*
  |--------------------------------------------------------------------------
  | FETCH PRODUCTS
  |--------------------------------------------------------------------------
  */

  const fetchProducts = useCallback(
    async () => {

      try {

        setLoading(true);

        const response =
          await api.get(
            "/api/products/",
            {
              params: {
                page_size: 1000,
                _qr_refresh:
                  Date.now(),
              },
            }
          );


        const rawData =
          response.data?.results ||
          response.data ||
          [];


        const data =
          Array.isArray(rawData)
            ? rawData
            : [];


        const normalizedProducts =
          data.map(
            normalizeProduct
          );


        setProducts(
          normalizedProducts
        );


        console.log(
          "PRODUCT QR DATA:",
          normalizedProducts.map(
            (product) => ({
              id: product.id,
              name: product.name,
              price: product.price,
              unit_type:
                product.unit_type,
              units_per_carton:
                product.units_per_carton,
              qr_token:
                product.qr_token,
            })
          )
        );

      } catch (error) {

        console.error(
          "PRODUCT QR FETCH ERROR:",
          error
        );

        Swal.fire({
          icon: "error",
          title:
            "Unable to Load Products",
          text:
            error?.response?.data?.detail ||
            error?.response?.data?.error ||
            "Could not load products from the backend.",
        });

      } finally {

        setLoading(false);

      }

    },
    [normalizeProduct]
  );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    fetchProducts();

  }, [fetchProducts]);


  /*
  |--------------------------------------------------------------------------
  | PRICE OPTIONS
  |--------------------------------------------------------------------------
  */

  const priceOptions = useMemo(
    () => {

      const prices =
        products
          .map(
            (product) =>
              Number(
                product.price
              )
          )
          .filter(
            (price) =>
              Number.isFinite(price)
          );


      const uniquePrices =
        [...new Set(prices)];


      return uniquePrices
        .sort(
          (a, b) => a - b
        )
        .map(
          (price) => ({
            value: price,

            label:
              `$${price.toFixed(2)}`,
          })
        );

    },
    [products]
  );


  /*
  |--------------------------------------------------------------------------
  | PRODUCTS WITH SELECTED PRICE
  |--------------------------------------------------------------------------
  */

  const selectedProducts =
    useMemo(
      () => {

        if (
          selectedPrice === null ||
          selectedPrice === undefined
        ) {
          return [];
        }


        return products.filter(
          (product) =>
            Number(
              product.price
            ) ===
            Number(
              selectedPrice.value
            )
        );

      },
      [
        products,
        selectedPrice,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | GET NUMBER OF PHYSICAL UNITS
  |--------------------------------------------------------------------------
  |
  | CARTON:
  |     units_per_carton = current individual units
  |
  | SINGLE:
  |     units_per_carton = current individual units
  |
  */

  const getUnitCount = useCallback(
    (product) => {

      const units =
        Number(
          product?.units_per_carton
        );


      if (
        Number.isFinite(units) &&
        units > 0
      ) {
        return Math.floor(units);
      }


      return 0;

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | TOTAL PHYSICAL QR LABELS
  |--------------------------------------------------------------------------
  */

  const totalQRLabels =
    useMemo(
      () => {

        return selectedProducts.reduce(
          (
            total,
            product
          ) => {

            return (
              total +
              getUnitCount(
                product
              )
            );

          },
          0
        );

      },
      [
        selectedProducts,
        getUnitCount,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | PRODUCTS WITHOUT QR
  |--------------------------------------------------------------------------
  */

  const productsWithoutQR =
    useMemo(
      () => {

        return selectedProducts.filter(
          (product) =>
            !getProductQRToken(
              product
            )
        );

      },
      [
        selectedProducts,
        getProductQRToken,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | PRODUCTS WITH QR
  |--------------------------------------------------------------------------
  */

  const productsWithQR =
    useMemo(
      () => {

        return selectedProducts.filter(
          (product) =>
            !!getProductQRToken(
              product
            )
        );

      },
      [
        selectedProducts,
        getProductQRToken,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | FILE NAME
  |--------------------------------------------------------------------------
  */

  const pdfFileName =
    useMemo(
      () => {

        if (!selectedPrice) {

          return (
            "Zuluwhiskey-Product-QR-Labels.pdf"
          );

        }


        const safePrice =
          Number(
            selectedPrice.value
          ).toFixed(2);


        return (
          `Zuluwhiskey-QR-Labels-$${safePrice}.pdf`
        );

      },
      [selectedPrice]
    );


  /*
  |--------------------------------------------------------------------------
  | REFRESH
  |--------------------------------------------------------------------------
  */

  const refreshProducts =
    async () => {

      await fetchProducts();

      Swal.fire({
        icon: "success",
        title:
          "Products Refreshed",
        text:
          "Product information is up to date.",
        timer: 1200,
        showConfirmButton: false,
      });

    };


  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD VALIDATION
  |--------------------------------------------------------------------------
  */

  const handleDownloadClick =
    () => {

      if (!selectedPrice) {

        Swal.fire({
          icon: "warning",
          title:
            "Select a Price",
          text:
            "Select a selling price first.",
        });

        return false;
      }


      if (!selectedProducts.length) {

        Swal.fire({
          icon: "warning",
          title:
            "No Products Found",
          text:
            "No products were found with this price.",
        });

        return false;
      }


      if (!totalQRLabels) {

        Swal.fire({
          icon: "warning",
          title:
            "No Units Available",
          text:
            "The selected products do not have any individual units available for QR printing.",
        });

        return false;
      }


      if (productsWithoutQR.length) {

        const missingNames =
          productsWithoutQR
            .map(
              (product) =>
                product.name
            )
            .join(", ");


        Swal.fire({
          icon: "warning",
          title:
            "Some Products Have No QR Token",

          html: `
            <div style="text-align:left">

              <p>
                <strong>
                  ${productsWithoutQR.length}
                </strong>
                product(s) do not have a QR token.
              </p>

              <p>
                Products:
                <strong>
                  ${missingNames}
                </strong>
              </p>

              <p>
                Please refresh the products and try again.
              </p>

            </div>
          `,
        });

        return false;

      }


      return true;

    };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <div
      style={{
        minHeight: "100vh",

        padding: "24px",

        background:
          "linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%)",
      }}
    >

      {/* =========================================================
          HEADER
      ========================================================== */}

      <Paper
        elevation={0}
        sx={{
          padding: {
            xs: 2,
            sm: 3,
          },

          borderRadius: 3,

          border:
            "1px solid #e5e7eb",

          background:
            "linear-gradient(135deg, #1B2A41 0%, #273A56 100%)",

          color: "#fff",

          marginBottom: 3,
        }}
      >

        <div
          style={{
            display: "flex",

            alignItems: "center",

            justifyContent:
              "space-between",

            gap: 20,

            flexWrap: "wrap",
          }}
        >

          <div
            style={{
              display: "flex",

              alignItems: "center",

              gap: 15,
            }}
          >

            <div
              style={{
                width: 58,

                height: 58,

                borderRadius: 16,

                display: "flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",

                background:
                  "rgba(255,255,255,0.12)",
              }}
            >

              <QrCode2
                sx={{
                  fontSize: 34,

                  color: "#fff",
                }}
              />

            </div>


            <div>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,

                  color: "#fff",
                }}
              >
                Product QR Labels
              </Typography>


              <Typography
                variant="body2"
                sx={{
                  color:
                    "rgba(255,255,255,0.72)",

                  marginTop: 0.5,
                }}
              >
                Generate one printable QR
                label for every individual
                unit by selling price
              </Typography>

            </div>

          </div>


          <Button
            variant="outlined"

            startIcon={
              <Refresh />
            }

            onClick={
              refreshProducts
            }

            disabled={
              loading
            }

            sx={{
              color: "#fff",

              borderColor:
                "rgba(255,255,255,0.35)",

              "&:hover": {
                borderColor: "#fff",

                background:
                  "rgba(255,255,255,0.08)",
              },
            }}
          >
            Refresh
          </Button>

        </div>

      </Paper>


      {/* =========================================================
          PRICE SELECTOR
      ========================================================== */}

      <Paper
        elevation={0}
        sx={{
          padding: {
            xs: 2,
            sm: 3,
          },

          borderRadius: 3,

          border:
            "1px solid #e5e7eb",

          marginBottom: 3,
        }}
      >

        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,

            color: "#1B2A41",

            marginBottom: 0.5,
          }}
        >
          Select Selling Price
        </Typography>


        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            marginBottom: 2,
          }}
        >
          Every product with this exact
          selling price will be included,
          and each available individual
          unit will receive a QR label.
        </Typography>


        <Autocomplete
          options={
            priceOptions
          }

          value={
            selectedPrice
          }

          onChange={(
            event,
            newValue
          ) => {

            setSelectedPrice(
              newValue
            );

          }}

          getOptionLabel={
            (option) =>
              option?.label || ""
          }

          loading={
            loading
          }

          fullWidth

          renderInput={
            (params) => (

              <TextField
                {...params}

                label="Selling Price"

                placeholder=
                  "Choose price..."

                InputProps={{
                  ...params.InputProps,

                  startAdornment: (
                    <>
                      <AttachMoney
                        sx={{
                          color:
                            "#1B2A41",

                          marginRight:
                            1,
                        }}
                      />

                      {
                        params
                          .InputProps
                          .startAdornment
                      }
                    </>
                  ),

                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress
                          size={20}
                        />
                      ) : null}

                      {
                        params
                          .InputProps
                          .endAdornment
                      }
                    </>
                  ),
                }}
              />

            )
          }

          renderOption={
            (
              props,
              option
            ) => (

              <li
                {...props}
                key={
                  option.value
                }
              >

                <AttachMoney
                  sx={{
                    marginRight:
                      1,

                    color:
                      "#1B2A41",
                  }}
                />

                <strong>
                  {
                    option.label
                  }
                </strong>

              </li>

            )
          }

        />

      </Paper>


      {/* =========================================================
          STATISTICS
      ========================================================== */}

      <div
        style={{
          display: "grid",

          gridTemplateColumns:
            "repeat(auto-fit, minmax(180px, 1fr))",

          gap: 16,

          marginBottom: 24,
        }}
      >

        <Paper
          elevation={0}
          sx={{
            padding: 2.5,

            borderRadius: 3,

            border:
              "1px solid #e5e7eb",
          }}
        >

          <div
            style={{
              display: "flex",

              alignItems: "center",

              gap: 12,
            }}
          >

            <Inventory2
              sx={{
                color:
                  "#1B2A41",

                fontSize: 30,
              }}
            />


            <div>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Products
              </Typography>


              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,

                  color:
                    "#1B2A41",
                }}
              >
                {
                  selectedProducts.length
                }
              </Typography>

            </div>

          </div>

        </Paper>


        <Paper
          elevation={0}
          sx={{
            padding: 2.5,

            borderRadius: 3,

            border:
              "1px solid #e5e7eb",
          }}
        >

          <div
            style={{
              display: "flex",

              alignItems:
                "center",

              gap: 12,
            }}
          >

            <AttachMoney
              sx={{
                color:
                  "#C8A74E",

                fontSize: 32,
              }}
            />


            <div>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Selected Price
              </Typography>


              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,

                  color:
                    "#1B2A41",
                }}
              >

                {selectedPrice
                  ? `$${Number(
                      selectedPrice.value
                    ).toFixed(2)}`
                  : "--"}

              </Typography>

            </div>

          </div>

        </Paper>


        {/* =====================================================
            TOTAL PHYSICAL QR LABELS
        ====================================================== */}

        <Paper
          elevation={0}
          sx={{
            padding: 2.5,

            borderRadius: 3,

            border:
              "1px solid #e5e7eb",
          }}
        >

          <div
            style={{
              display: "flex",

              alignItems:
                "center",

              gap: 12,
            }}
          >

            <QrCode2
              sx={{
                color:
                  "#1B2A41",

                fontSize: 30,
              }}
            />


            <div>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Physical QR Labels
              </Typography>


              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,

                  color:
                    "#1B2A41",
                }}
              >
                {
                  totalQRLabels
                }
              </Typography>

            </div>

          </div>

        </Paper>

      </div>


      {/* =========================================================
          SELECTED PRODUCTS
      ========================================================== */}

      {selectedPrice && (

        <Paper
          elevation={0}
          sx={{
            padding: {
              xs: 2,
              sm: 3,
            },

            borderRadius: 3,

            border:
              "1px solid #e5e7eb",

            marginBottom: 3,
          }}
        >

          <div
            style={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              gap: 15,

              flexWrap:
                "wrap",
            }}
          >

            <div>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,

                  color:
                    "#1B2A41",
                }}
              >
                Products at $
                {
                  Number(
                    selectedPrice.value
                  ).toFixed(2)
                }
              </Typography>


              <Typography
                variant="body2"
                color="text.secondary"
              >
                {
                  selectedProducts.length
                }
                {" "}
                product(s) found —{" "}
                {
                  totalQRLabels
                }
                {" "}
                physical unit QR labels
                will be printed.
              </Typography>

            </div>


            <Chip
              icon={
                <QrCode2 />
              }

              label={
                `${totalQRLabels} QR Labels`
              }

              sx={{
                fontWeight: 700,
              }}
            />

          </div>


          <Divider
            sx={{
              marginTop: 2,

              marginBottom: 2,
            }}
          />


          {selectedProducts.length === 0 ? (

            <Typography
              color="text.secondary"
            >
              No products found for this
              price.
            </Typography>

          ) : (

            <div
              style={{
                display: "grid",

                gridTemplateColumns:
                  "repeat(auto-fill, minmax(240px, 1fr))",

                gap: 12,
              }}
            >

              {
                selectedProducts.map(
                  (product) => {

                    const qrToken =
                      getProductQRToken(
                        product
                      );

                    const unitCount =
                      getUnitCount(
                        product
                      );


                    return (

                      <div
                        key={
                          product.id
                        }

                        style={{
                          padding: 14,

                          border:
                            "1px solid #e5e7eb",

                          borderRadius: 12,

                          background:
                            "#fafafa",
                        }}
                      >

                        <div
                          style={{
                            display: "flex",

                            alignItems:
                              "center",

                            gap: 12,
                          }}
                        >

                          <QrCode2
                            sx={{
                              color:
                                qrToken
                                  ? "#1B2A41"
                                  : "#b45309",

                              fontSize: 32,
                            }}
                          />


                          <div
                            style={{
                              minWidth: 0,
                            }}
                          >

                            <Typography
                              sx={{
                                fontWeight:
                                  700,

                                overflow:
                                  "hidden",

                                textOverflow:
                                  "ellipsis",

                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {
                                product.name
                              }
                            </Typography>


                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              $
                              {
                                Number(
                                  product.price
                                ).toFixed(2)
                              }
                            </Typography>


                            <Typography
                              variant="caption"
                              display="block"
                              sx={{
                                color:
                                  qrToken
                                    ? "#15803d"
                                    : "#b45309",

                                fontWeight:
                                  700,
                              }}
                            >
                              {
                                qrToken
                                  ? "QR Ready"
                                  : "QR Token Missing"
                              }
                            </Typography>


                            <Typography
                              variant="caption"
                              display="block"
                              sx={{
                                color:
                                  "#1B2A41",

                                fontWeight:
                                  700,
                              }}
                            >
                              {
                                unitCount
                              }
                              {" "}
                              unit QR labels
                            </Typography>

                          </div>

                        </div>

                      </div>

                    );

                  }
                )
              }

            </div>

          )}

        </Paper>

      )}


      {/* =========================================================
          DOWNLOAD
      ========================================================== */}

      <Paper
        elevation={0}
        sx={{
          padding: {
            xs: 2,
            sm: 3,
          },

          borderRadius: 3,

          border:
            "1px solid #e5e7eb",

          textAlign:
            "center",
        }}
      >

        <QrCode2
          sx={{
            fontSize: 48,

            color:
              "#1B2A41",

            marginBottom: 1,
          }}
        />


        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,

            color:
              "#1B2A41",
          }}
        >
          Generate Product QR Labels
        </Typography>


        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 650,

            margin:
              "6px auto 20px",
          }}
        >
          Every selected product will be
          expanded according to its current
          individual unit stock. For example,
          a product with 20 available units
          produces 20 printable QR labels.
        </Typography>


        {selectedProducts.length > 0 &&
        productsWithoutQR.length === 0 &&
        totalQRLabels > 0 ? (

          <PDFDownloadLink
            document={
              <ProductListQRScanPDF
                products={
                  selectedProducts
                }

                selectedPrice={
                  selectedPrice.value
                }
              />
            }

            fileName={
              pdfFileName
            }

            style={{
              textDecoration:
                "none",
            }}
          >

            {({
              loading:
                pdfLoading,
            }) => (

              <Button
                variant="contained"

                size="large"

                startIcon={
                  pdfLoading ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                    />
                  ) : (
                    <Download />
                  )
                }

                disabled={
                  pdfLoading ||
                  loading
                }

                sx={{
                  minWidth: {
                    xs: "100%",
                    sm: 280,
                  },

                  padding:
                    "12px 24px",

                  borderRadius: 2,

                  fontWeight: 800,

                  background:
                    "#1B2A41",

                  "&:hover": {
                    background:
                      "#273A56",
                  },
                }}
              >

                {pdfLoading
                  ? "Preparing PDF..."
                  : `Download ${totalQRLabels} QR Labels`}

              </Button>

            )}

          </PDFDownloadLink>

        ) : (

          <Button
            variant="contained"

            size="large"

            startIcon={
              <Download />
            }

            onClick={
              handleDownloadClick
            }

            sx={{
              minWidth: {
                xs: "100%",
                sm: 280,
              },

              padding:
                "12px 24px",

              borderRadius: 2,

              fontWeight: 800,

              background:
                "#1B2A41",

              "&:hover": {
                background:
                  "#273A56",
              },
            }}
          >
            Download QR Labels
          </Button>

        )}


        {productsWithoutQR.length > 0 && (

          <Typography
            variant="body2"
            sx={{
              marginTop: 2,

              color:
                "#b45309",

              fontWeight:
                600,
            }}
          >
            {
              productsWithoutQR.length
            }
            {" "}
            selected product(s) do not
            have a QR token.
          </Typography>

        )}

      </Paper>

    </div>

  );

};


export default ProductQRCode;






// import React, {
//   useCallback,
//   useEffect,
//   useMemo,
//   useState,
// } from "react";

// import api from "../../../api/api";

// import Swal from "sweetalert2";

// import {
//   Autocomplete,
//   TextField,
//   Button,
//   CircularProgress,
//   Paper,
//   Typography,
//   Chip,
//   Divider,
// } from "@mui/material";

// import {
//   QrCode2,
//   Download,
//   Inventory2,
//   AttachMoney,
//   Refresh,
// } from "@mui/icons-material";

// import {
//   PDFDownloadLink,
// } from "@react-pdf/renderer";

// import ProductListQRScanPDF from "./ProductListQRScanPDF";


// /*
// |--------------------------------------------------------------------------
// | PRODUCT QR MANAGEMENT
// |--------------------------------------------------------------------------
// */

// const ProductQRCode = () => {

//   /*
//   |--------------------------------------------------------------------------
//   | PRODUCTS
//   |--------------------------------------------------------------------------
//   */

//   const [products, setProducts] = useState([]);

//   const [loading, setLoading] = useState(false);


//   /*
//   |--------------------------------------------------------------------------
//   | PRICE
//   |--------------------------------------------------------------------------
//   */

//   const [selectedPrice, setSelectedPrice] = useState(null);


//   /*
//   |--------------------------------------------------------------------------
//   | GET QR TOKEN
//   |--------------------------------------------------------------------------
//   |
//   | The backend should normally return:
//   |
//   |     product.qr_token
//   |
//   | But we also support:
//   |
//   |     product.qr_payload.qr_token
//   |
//   | This makes the frontend resilient if the API serializer exposes
//   | the token through qr_payload.
//   |
//   |--------------------------------------------------------------------------
//   */

//   const getProductQRToken = useCallback((product) => {

//     if (!product) {
//       return null;
//     }

//     /*
//     | Direct QR token
//     */

//     if (
//       product.qr_token !== null &&
//       product.qr_token !== undefined &&
//       String(product.qr_token).trim() !== ""
//     ) {
//       return String(product.qr_token).trim();
//     }


//     /*
//     | QR payload fallback
//     */

//     if (
//       product.qr_payload &&
//       product.qr_payload.qr_token
//     ) {
//       return String(
//         product.qr_payload.qr_token
//       ).trim();
//     }


//     return null;

//   }, []);


//   /*
//   |--------------------------------------------------------------------------
//   | NORMALIZE PRODUCT
//   |--------------------------------------------------------------------------
//   */

//   const normalizeProduct = useCallback(
//     (product) => {

//       const qrToken =
//         getProductQRToken(product);

//       return {
//         ...product,

//         /*
//         | Always expose the token in one predictable frontend property.
//         */

//         qr_token: qrToken,

//         /*
//         | Make sure qr_payload also has the token.
//         */

//         qr_payload: {
//           ...(product.qr_payload || {}),

//           type:
//             product.qr_payload?.type ||
//             "product",

//           version:
//             product.qr_payload?.version ||
//             1,

//           product_id:
//             product.qr_payload?.product_id ||
//             product.id,

//           qr_token:
//             qrToken,
//         },
//       };

//     },
//     [getProductQRToken]
//   );


//   /*
//   |--------------------------------------------------------------------------
//   | FETCH PRODUCTS
//   |--------------------------------------------------------------------------
//   */

// const fetchProducts = useCallback(
//   async () => {

//     try {

//       setLoading(true);

//       const response = await api.get(
//         "/api/products/",
//         {
//           params: {
//             page_size: 1000,
//             _qr_refresh: Date.now(),
//           },
//         }
//       );

//       /*
//       |------------------------------------------------------------------
//       | EXTRACT DATA
//       |------------------------------------------------------------------
//       */

//       const rawData =
//         response.data?.results ||
//         response.data ||
//         [];

//       const data =
//         Array.isArray(rawData)
//           ? rawData
//           : [];

//       /*
//       |------------------------------------------------------------------
//       | NORMALIZE PRODUCTS
//       |------------------------------------------------------------------
//       */

//       const normalizedProducts =
//         data.map(normalizeProduct);

//       setProducts(
//         normalizedProducts
//       );

//       /*
//       |------------------------------------------------------------------
//       | DEBUG
//       |------------------------------------------------------------------
//       */

//       console.log(
//         "PRODUCT QR DATA:",
//         normalizedProducts.map(
//           (product) => ({
//             id: product.id,
//             name: product.name,
//             qr_token:
//               product.qr_token,
//             qr_payload:
//               product.qr_payload,
//           })
//         )
//       );

//     } catch (error) {

//       console.error(
//         "PRODUCT QR FETCH ERROR:",
//         error
//       );

//       Swal.fire({
//         icon: "error",
//         title: "Unable to Load Products",
//         text:
//           error?.response?.data?.detail ||
//           error?.response?.data?.error ||
//           "Could not load products from the backend.",
//       });

//     } finally {

//       setLoading(false);

//     }

//   },
//   [normalizeProduct]
// );


//   /*
//   |--------------------------------------------------------------------------
//   | INITIAL LOAD
//   |--------------------------------------------------------------------------
//   */

//   useEffect(() => {

//     fetchProducts();

//   }, [fetchProducts]);


//   /*
//   |--------------------------------------------------------------------------
//   | UNIQUE PRICE OPTIONS
//   |--------------------------------------------------------------------------
//   */

//   const priceOptions = useMemo(() => {

//     const prices = products
//       .map(
//         (product) =>
//           Number(product.price)
//       )
//       .filter(
//         (price) =>
//           Number.isFinite(price)
//       );


//     const uniquePrices =
//       [...new Set(prices)];


//     return uniquePrices
//       .sort(
//         (a, b) => a - b
//       )
//       .map(
//         (price) => ({
//           value: price,
//           label:
//             `$${price.toFixed(2)}`,
//         })
//       );

//   }, [products]);


//   /*
//   |--------------------------------------------------------------------------
//   | PRODUCTS FOR SELECTED PRICE
//   |--------------------------------------------------------------------------
//   */

//   const selectedProducts =
//     useMemo(() => {

//       if (
//         selectedPrice === null ||
//         selectedPrice === undefined
//       ) {
//         return [];
//       }


//       return products.filter(
//         (product) =>
//           Number(product.price) ===
//           Number(
//             selectedPrice.value
//           )
//       );

//     }, [
//       products,
//       selectedPrice,
//     ]);


//   /*
//   |--------------------------------------------------------------------------
//   | PRODUCTS WITHOUT QR
//   |--------------------------------------------------------------------------
//   */

//   const productsWithoutQR =
//     useMemo(() => {

//       return selectedProducts.filter(
//         (product) =>
//           !getProductQRToken(product)
//       );

//     }, [
//       selectedProducts,
//       getProductQRToken,
//     ]);


//   /*
//   |--------------------------------------------------------------------------
//   | PRODUCTS WITH QR
//   |--------------------------------------------------------------------------
//   */

//   const productsWithQR =
//     useMemo(() => {

//       return selectedProducts.filter(
//         (product) =>
//           !!getProductQRToken(product)
//       );

//     }, [
//       selectedProducts,
//       getProductQRToken,
//     ]);


//   /*
//   |--------------------------------------------------------------------------
//   | FILE NAME
//   |--------------------------------------------------------------------------
//   */

//   const pdfFileName = useMemo(() => {

//     if (!selectedPrice) {

//       return (
//         "Zuluwhiskey-Product-QR-Labels.pdf"
//       );

//     }


//     const safePrice =
//       Number(
//         selectedPrice.value
//       ).toFixed(2);


//     return (
//       `Zuluwhiskey-QR-Labels-$${safePrice}.pdf`
//     );

//   }, [selectedPrice]);


//   /*
//   |--------------------------------------------------------------------------
//   | CLEAR PRICE
//   |--------------------------------------------------------------------------
//   */

//   const clearSelection = () => {

//     setSelectedPrice(null);

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | REFRESH
//   |--------------------------------------------------------------------------
//   */

//   const refreshProducts = async () => {

//     await fetchProducts();


//     Swal.fire({
//       icon: "success",
//       title: "Products Refreshed",
//       text:
//         "Product information is up to date.",
//       timer: 1200,
//       showConfirmButton: false,
//     });

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | DOWNLOAD VALIDATION
//   |--------------------------------------------------------------------------
//   */

//   const handleDownloadClick = () => {

//     /*
//     | No price
//     */

//     if (!selectedPrice) {

//       Swal.fire({
//         icon: "warning",
//         title: "Select a Price",
//         text:
//           "Select a selling price first.",
//       });

//       return false;

//     }


//     /*
//     | No products
//     */

//     if (!selectedProducts.length) {

//       Swal.fire({
//         icon: "warning",
//         title: "No Products Found",
//         text:
//           "No products were found with this price.",
//       });

//       return false;

//     }


//     /*
//     | Missing QR tokens
//     */

//     if (productsWithoutQR.length) {

//       const missingNames =
//         productsWithoutQR
//           .map(
//             (product) =>
//               product.name
//           )
//           .join(", ");


//       Swal.fire({
//         icon: "warning",
//         title:
//           "Some Products Have No QR Token",

//         html: `
//           <div style="text-align:left">

//             <p>
//               <strong>
//                 ${productsWithoutQR.length}
//               </strong>
//               product(s) do not have a QR token
//               in the API response.
//             </p>

//             <p>
//               Products:
//               <strong>
//                 ${missingNames}
//               </strong>
//             </p>

//             <p>
//               Please refresh the products and try again.
//             </p>

//           </div>
//         `,
//       });

//       return false;

//     }


//     return true;

//   };


//   /*
//   |--------------------------------------------------------------------------
//   | RENDER
//   |--------------------------------------------------------------------------
//   */

//   return (

//     <div
//       style={{
//         minHeight: "100vh",

//         padding: "24px",

//         background:
//           "linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%)",
//       }}
//     >

//       {/* =====================================================
//           HEADER
//       ====================================================== */}

//       <Paper
//         elevation={0}
//         sx={{
//           padding: {
//             xs: 2,
//             sm: 3,
//           },

//           borderRadius: 3,

//           border:
//             "1px solid #e5e7eb",

//           background:
//             "linear-gradient(135deg, #1B2A41 0%, #273A56 100%)",

//           color: "#fff",

//           marginBottom: 3,
//         }}
//       >

//         <div
//           style={{
//             display: "flex",

//             alignItems: "center",

//             justifyContent:
//               "space-between",

//             gap: 20,

//             flexWrap: "wrap",
//           }}
//         >

//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 15,
//             }}
//           >

//             <div
//               style={{
//                 width: 58,
//                 height: 58,

//                 borderRadius: 16,

//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",

//                 background:
//                   "rgba(255,255,255,0.12)",
//               }}
//             >

//               <QrCode2
//                 sx={{
//                   fontSize: 34,
//                   color: "#fff",
//                 }}
//               />

//             </div>


//             <div>

//               <Typography
//                 variant="h5"
//                 sx={{
//                   fontWeight: 800,
//                   color: "#fff",
//                 }}
//               >
//                 Product QR Labels
//               </Typography>


//               <Typography
//                 variant="body2"
//                 sx={{
//                   color:
//                     "rgba(255,255,255,0.72)",

//                   marginTop: 0.5,
//                 }}
//               >
//                 Generate printable scanning
//                 labels by selling price
//               </Typography>

//             </div>

//           </div>


//           <Button
//             variant="outlined"
//             startIcon={<Refresh />}
//             onClick={refreshProducts}
//             disabled={loading}
//             sx={{
//               color: "#fff",

//               borderColor:
//                 "rgba(255,255,255,0.35)",

//               "&:hover": {
//                 borderColor: "#fff",

//                 background:
//                   "rgba(255,255,255,0.08)",
//               },
//             }}
//           >
//             Refresh
//           </Button>

//         </div>

//       </Paper>


//       {/* =====================================================
//           CONTROL PANEL
//       ====================================================== */}

//       <Paper
//         elevation={0}
//         sx={{
//           padding: {
//             xs: 2,
//             sm: 3,
//           },

//           borderRadius: 3,

//           border:
//             "1px solid #e5e7eb",

//           marginBottom: 3,
//         }}
//       >

//         <Typography
//           variant="h6"
//           sx={{
//             fontWeight: 800,
//             color: "#1B2A41",
//             marginBottom: 0.5,
//           }}
//         >
//           Select Selling Price
//         </Typography>


//         <Typography
//           variant="body2"
//           color="text.secondary"
//           sx={{
//             marginBottom: 2,
//           }}
//         >
//           Select a price and the system will
//           automatically collect every product
//           with that exact selling price.
//         </Typography>


//         <Autocomplete
//           options={priceOptions}

//           value={selectedPrice}

//           onChange={(
//             event,
//             newValue
//           ) => {

//             setSelectedPrice(
//               newValue
//             );

//           }}

//           getOptionLabel={(option) =>
//             option?.label || ""
//           }

//           loading={loading}

//           fullWidth

//           renderInput={(params) => (

//             <TextField
//               {...params}

//               label="Selling Price"

//               placeholder="Choose price..."

//               InputProps={{
//                 ...params.InputProps,

//                 startAdornment: (
//                   <>
//                     <AttachMoney
//                       sx={{
//                         color: "#1B2A41",
//                         marginRight: 1,
//                       }}
//                     />

//                     {
//                       params
//                         .InputProps
//                         .startAdornment
//                     }
//                   </>
//                 ),

//                 endAdornment: (
//                   <>
//                     {loading ? (
//                       <CircularProgress
//                         size={20}
//                       />
//                     ) : null}

//                     {
//                       params
//                         .InputProps
//                         .endAdornment
//                     }
//                   </>
//                 ),
//               }}
//             />

//           )}

//           renderOption={(
//             props,
//             option
//           ) => (

//             <li
//               {...props}
//               key={option.value}
//             >

//               <AttachMoney
//                 sx={{
//                   marginRight: 1,
//                   color: "#1B2A41",
//                 }}
//               />

//               <strong>
//                 {option.label}
//               </strong>

//             </li>

//           )}

//         />

//       </Paper>


//       {/* =====================================================
//           STATISTICS
//       ====================================================== */}

//       <div
//         style={{
//           display: "grid",

//           gridTemplateColumns:
//             "repeat(auto-fit, minmax(180px, 1fr))",

//           gap: 16,

//           marginBottom: 24,
//         }}
//       >

//         {/* TOTAL PRODUCTS */}

//         <Paper
//           elevation={0}
//           sx={{
//             padding: 2.5,
//             borderRadius: 3,
//             border:
//               "1px solid #e5e7eb",
//           }}
//         >

//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 12,
//             }}
//           >

//             <Inventory2
//               sx={{
//                 color: "#1B2A41",
//                 fontSize: 30,
//               }}
//             />

//             <div>

//               <Typography
//                 variant="caption"
//                 color="text.secondary"
//               >
//                 Products
//               </Typography>


//               <Typography
//                 variant="h5"
//                 sx={{
//                   fontWeight: 800,
//                   color: "#1B2A41",
//                 }}
//               >
//                 {selectedProducts.length}
//               </Typography>

//             </div>

//           </div>

//         </Paper>


//         {/* SELECTED PRICE */}

//         <Paper
//           elevation={0}
//           sx={{
//             padding: 2.5,
//             borderRadius: 3,
//             border:
//               "1px solid #e5e7eb",
//           }}
//         >

//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 12,
//             }}
//           >

//             <AttachMoney
//               sx={{
//                 color: "#C8A74E",
//                 fontSize: 32,
//               }}
//             />

//             <div>

//               <Typography
//                 variant="caption"
//                 color="text.secondary"
//               >
//                 Selected Price
//               </Typography>


//               <Typography
//                 variant="h5"
//                 sx={{
//                   fontWeight: 800,
//                   color: "#1B2A41",
//                 }}
//               >

//                 {selectedPrice
//                   ? `$${Number(
//                       selectedPrice.value
//                     ).toFixed(2)}`
//                   : "--"}

//               </Typography>

//             </div>

//           </div>

//         </Paper>


//         {/* QR LABELS */}

//         <Paper
//           elevation={0}
//           sx={{
//             padding: 2.5,
//             borderRadius: 3,
//             border:
//               "1px solid #e5e7eb",
//           }}
//         >

//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               gap: 12,
//             }}
//           >

//             <QrCode2
//               sx={{
//                 color: "#1B2A41",
//                 fontSize: 30,
//               }}
//             />

//             <div>

//               <Typography
//                 variant="caption"
//                 color="text.secondary"
//               >
//                 QR Labels
//               </Typography>


//               <Typography
//                 variant="h5"
//                 sx={{
//                   fontWeight: 800,
//                   color: "#1B2A41",
//                 }}
//               >
//                 {productsWithQR.length}
//               </Typography>

//             </div>

//           </div>

//         </Paper>

//       </div>


//       {/* =====================================================
//           SELECTED PRODUCTS
//       ====================================================== */}

//       {selectedPrice && (

//         <Paper
//           elevation={0}
//           sx={{
//             padding: {
//               xs: 2,
//               sm: 3,
//             },

//             borderRadius: 3,

//             border:
//               "1px solid #e5e7eb",

//             marginBottom: 3,
//           }}
//         >

//           <div
//             style={{
//               display: "flex",

//               justifyContent:
//                 "space-between",

//               alignItems: "center",

//               gap: 15,

//               flexWrap: "wrap",
//             }}
//           >

//             <div>

//               <Typography
//                 variant="h6"
//                 sx={{
//                   fontWeight: 800,
//                   color: "#1B2A41",
//                 }}
//               >
//                 Products at $
//                 {Number(
//                   selectedPrice.value
//                 ).toFixed(2)}
//               </Typography>


//               <Typography
//                 variant="body2"
//                 color="text.secondary"
//               >
//                 {selectedProducts.length}
//                 {" "}
//                 product(s) will be included
//                 in the PDF.
//               </Typography>

//             </div>


//             <Chip
//               icon={<QrCode2 />}
//               label={`${productsWithQR.length} QR Labels`}
//               sx={{
//                 fontWeight: 700,
//               }}
//             />

//           </div>


//           <Divider
//             sx={{
//               marginTop: 2,
//               marginBottom: 2,
//             }}
//           />


//           {selectedProducts.length === 0 ? (

//             <Typography
//               color="text.secondary"
//             >
//               No products found for this
//               price.
//             </Typography>

//           ) : (

//             <div
//               style={{
//                 display: "grid",

//                 gridTemplateColumns:
//                   "repeat(auto-fill, minmax(220px, 1fr))",

//                 gap: 12,
//               }}
//             >

//               {selectedProducts.map(
//                 (product) => {

//                   const qrToken =
//                     getProductQRToken(
//                       product
//                     );


//                   return (

//                     <div
//                       key={product.id}
//                       style={{
//                         padding: 14,

//                         border:
//                           "1px solid #e5e7eb",

//                         borderRadius: 12,

//                         display: "flex",

//                         alignItems: "center",

//                         gap: 12,

//                         background:
//                           "#fafafa",
//                       }}
//                     >

//                       <QrCode2
//                         sx={{
//                           color:
//                             qrToken
//                               ? "#1B2A41"
//                               : "#b45309",

//                           fontSize: 32,
//                         }}
//                       />


//                       <div
//                         style={{
//                           minWidth: 0,
//                         }}
//                       >

//                         <Typography
//                           sx={{
//                             fontWeight: 700,

//                             overflow:
//                               "hidden",

//                             textOverflow:
//                               "ellipsis",

//                             whiteSpace:
//                               "nowrap",
//                           }}
//                         >
//                           {product.name}
//                         </Typography>


//                         <Typography
//                           variant="caption"
//                           color="text.secondary"
//                         >
//                           $
//                           {Number(
//                             product.price
//                           ).toFixed(2)}
//                         </Typography>


//                         <Typography
//                           variant="caption"
//                           display="block"
//                           sx={{
//                             color:
//                               qrToken
//                                 ? "#15803d"
//                                 : "#b45309",

//                             fontWeight: 700,
//                           }}
//                         >
//                           {qrToken
//                             ? "QR Ready"
//                             : "QR Token Missing"}
//                         </Typography>

//                       </div>

//                     </div>

//                   );

//                 }
//               )}

//             </div>

//           )}

//         </Paper>

//       )}


//       {/* =====================================================
//           DOWNLOAD
//       ====================================================== */}

//       <Paper
//         elevation={0}
//         sx={{
//           padding: {
//             xs: 2,
//             sm: 3,
//           },

//           borderRadius: 3,

//           border:
//             "1px solid #e5e7eb",

//           textAlign: "center",
//         }}
//       >

//         <QrCode2
//           sx={{
//             fontSize: 48,
//             color: "#1B2A41",
//             marginBottom: 1,
//           }}
//         />


//         <Typography
//           variant="h6"
//           sx={{
//             fontWeight: 800,
//             color: "#1B2A41",
//           }}
//         >
//           Generate Product QR Labels
//         </Typography>


//         <Typography
//           variant="body2"
//           color="text.secondary"
//           sx={{
//             maxWidth: 600,
//             margin:
//               "6px auto 20px",
//           }}
//         >
//           The PDF will contain one
//           professionally bordered label
//           for every product matching the
//           selected selling price.
//         </Typography>


//         {selectedProducts.length > 0 &&
//         productsWithoutQR.length === 0 ? (

//           <PDFDownloadLink
//             document={
//               <ProductListQRScanPDF
//                 products={
//                   selectedProducts
//                 }

//                 selectedPrice={
//                   selectedPrice.value
//                 }
//               />
//             }

//             fileName={
//               pdfFileName
//             }

//             style={{
//               textDecoration: "none",
//             }}
//           >

//             {({
//               loading: pdfLoading,
//             }) => (

//               <Button
//                 variant="contained"
//                 size="large"

//                 startIcon={
//                   pdfLoading ? (
//                     <CircularProgress
//                       size={20}
//                       color="inherit"
//                     />
//                   ) : (
//                     <Download />
//                   )
//                 }

//                 disabled={
//                   pdfLoading ||
//                   loading
//                 }

//                 sx={{
//                   minWidth: {
//                     xs: "100%",
//                     sm: 280,
//                   },

//                   padding:
//                     "12px 24px",

//                   borderRadius: 2,

//                   fontWeight: 800,

//                   background:
//                     "#1B2A41",

//                   "&:hover": {
//                     background:
//                       "#273A56",
//                   },
//                 }}
//               >

//                 {pdfLoading
//                   ? "Preparing PDF..."
//                   : "Download QR Labels"}

//               </Button>

//             )}

//           </PDFDownloadLink>

//         ) : (

//           <Button
//             variant="contained"
//             size="large"

//             startIcon={
//               <Download />
//             }

//             onClick={
//               handleDownloadClick
//             }

//             sx={{
//               minWidth: {
//                 xs: "100%",
//                 sm: 280,
//               },

//               padding:
//                 "12px 24px",

//               borderRadius: 2,

//               fontWeight: 800,

//               background:
//                 "#1B2A41",

//               "&:hover": {
//                 background:
//                   "#273A56",
//               },
//             }}
//           >
//             Download QR Labels
//           </Button>

//         )}


//         {productsWithoutQR.length > 0 && (

//           <Typography
//             variant="body2"
//             sx={{
//               marginTop: 2,
//               color: "#b45309",
//               fontWeight: 600,
//             }}
//           >
//             {productsWithoutQR.length}
//             {" "}
//             selected product(s) do not
//             have a QR token in the API
//             response.
//           </Typography>

//         )}

//       </Paper>

//     </div>

//   );

// };

// export default ProductQRCode;

